import React, {
  useState, useEffect, useCallback, useMemo, useRef,
} from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { matchSorter } from 'match-sorter';
import styled from '@emotion/styled';
import PropTypes from 'prop-types';

import Modal from '../../../../../shared/_TBComponents/Modal';
import Icon from '../../../../../shared/_TBComponents/Icon';
import {
  fetchCalendars, createCalendarEntry, fetchPlanCalendars,
} from '../../../../calendars/ducks/calendarsActions';
import {
  setActiveModal,
  setSelectedSavedWorkout,
  setSelectedReduxCalendar,
  setSelectedReduxAthlete,
  setProgramType,
  setSelectedDate,
} from '../../../../calendar/ducks/calendarActions';

// LoadProgramsModal opens on the /build route after mount, so the picker
// UI needs to survive the route transition briefly before triggering it.
const LOAD_PROGRAMS_MODAL_OPEN_DELAY_MS = 300;

// The Modal content wrapper is a flex row — children need width:100% or
// they collapse and leave dead whitespace next to the content.
const CalPickerBox = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 32px;
  box-sizing: border-box;
`;

const CalPickerHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  width: 100%;
`;

const CalPickerTitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`;

const CalPickerTitle = styled.h2`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 24px;
  font-weight: 900;
  color: #444444;
  margin: 0;
  letter-spacing: 0;
  line-height: 1.5;
  white-space: nowrap;
`;

const CalPickerClose = styled.button`
  appearance: none;
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  margin: -4px;
  width: 32px;
  height: 32px;
  color: #444444;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  flex-shrink: 0;

  &:hover {
    background: #F4F4F8;
  }
`;

const CalPickerSubtitle = styled.p`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 16px;
  font-weight: 400;
  color: #9E9E9E;
  margin: 0;
  line-height: 1.5;
  width: 100%;
`;

const CalPickerField = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  width: 100%;
`;

const CalPickerLabel = styled.label`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 16px;
  font-weight: 700;
  color: #444444;
  line-height: 1.5;
`;

// The wrapper owns the border + focus ring — not the inner input — so a
// focused input and its open dropdown read as one unified unit.
const CalPickerComboboxWrap = styled(({ isOpen, ...rest }) => <div {...rest} />)`
  position: relative;
  width: 100%;
  background: #FFFFFF;
  border: 1px solid ${(props) => (props.isOpen ? '#444444' : '#E0E0E0')};
  border-radius: 4px;
  display: flex;
  align-items: center;
  padding: 6px 8px 6px 12px;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
  box-shadow: ${(props) => (props.isOpen ? '0 0 0 3px rgba(68, 68, 68, 0.12)' : 'none')};

  &:hover {
    border-color: #C9C9D4;
  }
`;

const CalPickerSearchIcon = styled.span`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #9E9E9E;
  margin-right: 8px;
`;

const CalPickerComboboxInput = styled.input`
  flex: 1;
  min-width: 0;
  background: transparent;
  border: none;
  outline: none;
  padding: 0;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 16px;
  font-weight: 400;
  color: #444444;
  line-height: 1.5;

  &::placeholder {
    color: #9E9E9E;
  }
`;

const CalPickerClearButton = styled.button`
  appearance: none;
  background: none;
  border: none;
  color: #9E9E9E;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 4px;
  border-radius: 4px;
  flex-shrink: 0;

  &:hover {
    background: #F4F4F8;
    color: #444444;
  }
`;

const CalPickerChevron = styled(({ isOpen, ...rest }) => <span {...rest} />)`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  color: #444444;
  cursor: pointer;
  transition: transform 0.18s ease;
  transform: rotate(${(props) => (props.isOpen ? '180deg' : '0deg')});
`;

// No vertical padding: the group labels carry their own tinted-band
// background and must run edge-to-edge inside the panel.
const CalPickerDropdown = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  background: #FFFFFF;
  border: 1px solid #E0E0E0;
  border-radius: 4px;
  box-shadow: 0 12px 24px rgba(48, 49, 51, 0.12);
  max-height: 320px;
  overflow-y: auto;
  z-index: 10;
  padding: 0;
`;

// Tinted band + bottom border reuses the planner's GroupHeader treatment so
// the CALENDARS / ATHLETE CALENDARS split reads as familiar grouping.
const CalPickerDropdownGroupLabel = styled.div`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.36px;
  color: #9E9E9E;
  text-transform: uppercase;
  padding: 8px 16px;
  background-color: #F9F9F9;
  border-bottom: 1px solid #EEEEEE;
`;

const CalPickerDropdownOption = styled(({ isFocused, ...rest }) => (
  <button type='button' {...rest} />
))`
  display: block;
  width: 100%;
  text-align: left;
  background: ${(props) => (props.isFocused ? '#F4F4F6' : 'transparent')};
  border: none;
  padding: 8px 16px;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 16px;
  font-weight: 400;
  color: #444444;
  line-height: 1.5;
  cursor: pointer;

  &:hover {
    background: #F4F4F6;
  }
`;

const CalPickerDropdownEmpty = styled.div`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 14px;
  color: #9E9E9E;
  padding: 12px 16px;
  text-align: center;
`;

const CalPickerInput = styled.input`
  width: 100%;
  background: #FFFFFF;
  border: 1px solid #E0E0E0;
  border-radius: 4px;
  padding: 6px 56px 6px 16px;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 16px;
  font-weight: 400;
  color: #444444;
  line-height: 1.5;
  outline: none;
  box-sizing: border-box;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &::placeholder {
    color: #9E9E9E;
  }

  &:hover:not(:disabled) {
    border-color: #C9C9D4;
  }

  &:focus,
  &:focus-visible {
    border-color: #444444;
    box-shadow: 0 0 0 3px rgba(68, 68, 68, 0.12);
  }
`;

const CalPickerError = styled.p`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 13px;
  font-weight: 700;
  color: #C43D32;
  margin: 0;
  width: 100%;
`;

const CalPickerFooter = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 16px;
`;

const CalPickerCancel = styled.button`
  background: transparent;
  border: 2px solid #EEEEEE;
  border-radius: 4px;
  padding: 6px 16px;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 13px;
  font-weight: 700;
  color: #444444;
  letter-spacing: 0.39px;
  line-height: 1.5;
  cursor: pointer;
  transition: border-color 160ms ease, background 160ms ease;

  &:hover {
    border-color: #C9C9D4;
    background: #FAFAFB;
  }
`;

const CalPickerContinue = styled.button`
  background: ${(p) => (p.disabled ? '#E0E0E0' : '#444444')};
  color: ${(p) => (p.disabled ? '#757575' : '#FFFFFF')};
  border: none;
  border-radius: 4px;
  padding: 6px 16px;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.39px;
  line-height: 1.5;
  text-transform: uppercase;
  cursor: ${(p) => (p.disabled ? 'not-allowed' : 'pointer')};
  display: inline-flex;
  align-items: center;
  gap: 8px;
  transition: background 160ms ease, transform 160ms ease, box-shadow 160ms ease;

  &:hover:not(:disabled) {
    background: #2a2a2a;
    box-shadow: 0 4px 16px rgba(48, 49, 51, 0.25);
    transform: translateY(-1px);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }
`;

// Calendar picker for loading a saved program into a calendar. Continue
// routes to /build and opens the planner's LoadProgramsModal there.
const LoadToCalendarModal = ({
  isOpen, onClose, accountCode, program,
}) => {
  const history = useHistory();
  const dispatch = useDispatch();

  const unparsedCalendars = useSelector((state) => state.calendars.data.unparsedCalendars || []);

  // Selected calendar is a typed key (`cal-{id}` / `athlete-{userId}`) so
  // Continue can route accordingly; `calSearch` doubles as filter query and selected name.
  const [selectedCalKey, setSelectedCalKey] = useState('');
  const [calSearch, setCalSearch] = useState('');
  const [calDropdownOpen, setCalDropdownOpen] = useState(false);
  const [planCalendars, setPlanCalendars] = useState({ account: [], athlete: [] });
  const [isLoadingPlanCalendars, setIsLoadingPlanCalendars] = useState(false);
  const [newCalName, setNewCalName] = useState('');
  const calComboboxRef = useRef(null);
  const calInputRef = useRef(null);
  const [calError, setCalError] = useState('');
  const [isCreatingCalendar, setIsCreatingCalendar] = useState(false);

  // Each open starts from a blank picker; nothing lingers from the last use.
  useEffect(() => {
    if (!isOpen) return;
    setSelectedCalKey('');
    setCalSearch('');
    setCalDropdownOpen(false);
    setNewCalName('');
    setCalError('');
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && accountCode) {
      dispatch(fetchCalendars(accountCode));
    }
  }, [isOpen, accountCode]);

  // Fetch the typeahead calendar list from /plans/calendars — same endpoint
  // the planner's CalendarChooser uses, so the response shape is trusted.
  useEffect(() => {
    if (!isOpen || !accountCode) return undefined;
    let cancelled = false;
    setIsLoadingPlanCalendars(true);
    dispatch(fetchPlanCalendars(accountCode, {
      onSuccess: (data) => {
        if (cancelled) return;
        setPlanCalendars({
          account: Array.isArray(data.accountCalendars) ? data.accountCalendars : [],
          athlete: Array.isArray(data.athleteCalendars) ? data.athleteCalendars : [],
        });
        setIsLoadingPlanCalendars(false);
      },
      onError: () => {
        if (cancelled) return;
        setIsLoadingPlanCalendars(false);
      },
    }));
    return () => { cancelled = true; };
  }, [isOpen, accountCode, dispatch]);

  // Click-outside dismissal for the dropdown panel. Closes the panel only,
  // not the whole modal — clicking elsewhere inside the modal still works.
  useEffect(() => {
    if (!calDropdownOpen) return undefined;
    const handler = (e) => {
      if (calComboboxRef.current && !calComboboxRef.current.contains(e.target)) {
        setCalDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [calDropdownOpen]);

  // Shared by the "pick calendar" and "pick athlete" paths — both route to
  // /build and open LoadProgramsModal, differing only in the redux setter used.
  const navigateToLoadProgramsModal = useCallback((setEntity, entity) => {
    dispatch(setSelectedDate(new Date()));
    dispatch(setProgramType('calendar'));
    dispatch(setEntity(entity));
    if (program) {
      dispatch(setSelectedSavedWorkout(program));
    }
    history.push('/build');
    setTimeout(
      () => dispatch(setActiveModal('loadProgramsModal')),
      LOAD_PROGRAMS_MODAL_OPEN_DELAY_MS,
    );
  }, [program, dispatch, history]);

  const goToLoadModal = useCallback((calendarId) => {
    const calendar = unparsedCalendars.find((c) => c.id === calendarId) || { id: calendarId };
    navigateToLoadProgramsModal(setSelectedReduxCalendar, calendar);
  }, [unparsedCalendars, navigateToLoadProgramsModal]);

  // matchSorter filters across the visible label only, case-insensitive.
  // Empty query returns the full list so the dropdown works on first focus.
  const filteredCalOptions = useMemo(() => {
    const accountOptions = planCalendars.account.map((cal) => ({
      key: `cal-${cal.id}`,
      type: 'calendar',
      id: cal.id,
      label: cal.name || `Calendar ${cal.id}`,
      raw: cal,
    }));
    const athleteOptions = planCalendars.athlete.map((athlete) => ({
      key: `athlete-${athlete.id}`,
      type: 'athlete',
      id: athlete.id,
      label: athlete.name || `User ${athlete.id}`,
      raw: athlete,
    }));
    const trimmed = calSearch.trim();
    // If a calendar is selected and the input mirrors its label, treat the
    // search as empty — otherwise the dropdown filters down to one option.
    const selected = [...accountOptions, ...athleteOptions]
      .find((opt) => opt.key === selectedCalKey);
    const queryActive = trimmed && (!selected || selected.label !== trimmed);
    const filter = (list) => (queryActive ? matchSorter(list, trimmed, { keys: ['label'] }) : list);
    return {
      account: filter(accountOptions),
      athlete: filter(athleteOptions),
    };
  }, [planCalendars, calSearch, selectedCalKey]);

  // The two inputs are mutually exclusive: typing a new-calendar name clears
  // the calendar selection, and picking a calendar clears the typed name.
  const handlePickCalendar = useCallback((option) => {
    if (newCalName) setNewCalName('');
    setSelectedCalKey(option.key);
    setCalSearch(option.label);
    setCalDropdownOpen(false);
    setCalError('');
  }, [newCalName]);

  const handleSearchChange = useCallback((value) => {
    // Typing into the search clears any previous selection — the input is
    // the source of truth while the dropdown is open.
    if (selectedCalKey) setSelectedCalKey('');
    setCalSearch(value);
    setCalDropdownOpen(true);
    setCalError('');
  }, [selectedCalKey]);

  // Clearing must leave the picker closed because focusing the input reopens it.
  const handleClearCalendar = useCallback(() => {
    setSelectedCalKey('');
    setCalSearch('');
    setCalDropdownOpen(false);
    setCalError('');
  }, []);

  const handleTypeNew = useCallback((value) => {
    if (selectedCalKey) {
      setSelectedCalKey('');
      setCalSearch('');
    }
    setNewCalName(value);
    setCalError('');
  }, [selectedCalKey]);

  // One Continue handler dispatches whichever path is populated. Disabled
  // state on the button is the primary guard; this just routes.
  const handleContinue = useCallback(() => {
    if (selectedCalKey) {
      const selected = [
        ...planCalendars.account.map((cal) => ({ key: `cal-${cal.id}`, type: 'calendar', raw: cal })),
        ...planCalendars.athlete.map((a) => ({ key: `athlete-${a.id}`, type: 'athlete', raw: a })),
      ].find((opt) => opt.key === selectedCalKey);
      if (!selected) return;
      setCalError('');
      onClose();
      if (selected.type === 'athlete') {
        // Route athlete picks through the athlete-calendar slice — the
        // downstream LoadProgramsModal branches on which one is populated.
        navigateToLoadProgramsModal(setSelectedReduxAthlete, selected.raw);
      } else {
        goToLoadModal(Number(selected.raw.id));
      }
      return;
    }
    if (newCalName.trim()) {
      setCalError('');
      setIsCreatingCalendar(true);
      dispatch(createCalendarEntry(
        accountCode,
        { name: newCalName.trim(), workoutVisibility: 0 },
        {
          onSuccess: (data) => {
            setIsCreatingCalendar(false);
            onClose();
            goToLoadModal(Number(data.id));
          },
          onError: () => {
            setIsCreatingCalendar(false);
            setCalError('Unable to create calendar. Please try again.');
          },
        },
      ));
    }
  }, [
    selectedCalKey, planCalendars, newCalName, goToLoadModal, navigateToLoadProgramsModal,
    accountCode, dispatch, onClose,
  ]);

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      hideTitle
      hideButton
      customBorderRadius='4px'
      customPadding='32px'
      customMaxWidth='470px'
      customMinWidth='min(470px, calc(100vw - 48px))'
      customMinHeight='0'
    >
      <CalPickerBox>
        <CalPickerHeader>
          <CalPickerTitleRow>
            <CalPickerTitle>Load to Calendar</CalPickerTitle>
            <CalPickerClose
              type='button'
              aria-label='Close'
              onClick={onClose}
            >
              <svg width='16' height='16' viewBox='0 0 16 16' fill='none' aria-hidden='true'>
                <path
                  d='M4 4 L12 12 M12 4 L4 12'
                  stroke='currentColor'
                  strokeWidth='2'
                  strokeLinecap='round'
                />
              </svg>
            </CalPickerClose>
          </CalPickerTitleRow>
          <CalPickerSubtitle>
            Turn this program into a scheduled workout by loading it into an existing or new
            calendar.
          </CalPickerSubtitle>
        </CalPickerHeader>

        <CalPickerField>
          <CalPickerLabel htmlFor='cal-picker-select'>Select a calendar</CalPickerLabel>
          <CalPickerComboboxWrap
            ref={calComboboxRef}
            isOpen={calDropdownOpen}
          >
            <CalPickerSearchIcon aria-hidden='true'>
              <svg width='16' height='16' viewBox='0 0 24 24' fill='none'>
                <circle cx='11' cy='11' r='6.5' stroke='currentColor' strokeWidth='2' />
                <path d='M16 16 L20 20' stroke='currentColor' strokeWidth='2' strokeLinecap='round' />
              </svg>
            </CalPickerSearchIcon>
            <CalPickerComboboxInput
              ref={calInputRef}
              id='cal-picker-select'
              type='text'
              autoComplete='off'
              placeholder='Select a calendar'
              value={calSearch}
              onChange={(e) => handleSearchChange(e.target.value)}
              onFocus={() => setCalDropdownOpen(true)}
              onClick={() => setCalDropdownOpen(true)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  setCalDropdownOpen(false);
                }
              }}
            />
            {selectedCalKey && (
              <CalPickerClearButton
                type='button'
                aria-label='Clear selected calendar'
                onClick={handleClearCalendar}
              >
                <Icon icon='remove' size={16} color='currentColor' />
              </CalPickerClearButton>
            )}
            <CalPickerChevron
              isOpen={calDropdownOpen}
              role='button'
              aria-label='Toggle calendar list'
              onClick={() => setCalDropdownOpen((open) => !open)}
            >
              <svg width='16' height='16' viewBox='0 0 24 24' fill='none'>
                <path
                  d='M7 10 L12 15 L17 10'
                  stroke='currentColor'
                  strokeWidth='2'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
              </svg>
            </CalPickerChevron>
            {calDropdownOpen && (
              <CalPickerDropdown>
                {isLoadingPlanCalendars && (
                  <CalPickerDropdownEmpty>Loading calendars…</CalPickerDropdownEmpty>
                )}
                {!isLoadingPlanCalendars
                  && filteredCalOptions.account.length === 0
                  && filteredCalOptions.athlete.length === 0 && (
                    <CalPickerDropdownEmpty>
                      No calendars match your search
                    </CalPickerDropdownEmpty>
                )}
                {filteredCalOptions.account.length > 0 && (
                  <>
                    <CalPickerDropdownGroupLabel>Calendars</CalPickerDropdownGroupLabel>
                    {filteredCalOptions.account.map((opt) => (
                      <CalPickerDropdownOption
                        key={opt.key}
                        isFocused={selectedCalKey === opt.key}
                        onClick={() => handlePickCalendar(opt)}
                      >
                        {opt.label}
                      </CalPickerDropdownOption>
                    ))}
                  </>
                )}
                {filteredCalOptions.athlete.length > 0 && (
                  <>
                    <CalPickerDropdownGroupLabel>Athlete Calendars</CalPickerDropdownGroupLabel>
                    {filteredCalOptions.athlete.map((opt) => (
                      <CalPickerDropdownOption
                        key={opt.key}
                        isFocused={selectedCalKey === opt.key}
                        onClick={() => handlePickCalendar(opt)}
                      >
                        {opt.label}
                      </CalPickerDropdownOption>
                    ))}
                  </>
                )}
              </CalPickerDropdown>
            )}
          </CalPickerComboboxWrap>
        </CalPickerField>

        <CalPickerField>
          <CalPickerLabel htmlFor='cal-picker-new'>Or create a new calendar</CalPickerLabel>
          <CalPickerInput
            id='cal-picker-new'
            placeholder='Add a calendar title'
            value={newCalName}
            onChange={(e) => handleTypeNew(e.target.value)}
          />
        </CalPickerField>

        {calError && <CalPickerError>{calError}</CalPickerError>}

        <CalPickerFooter>
          <CalPickerCancel type='button' onClick={onClose}>
            Cancel
          </CalPickerCancel>
          <CalPickerContinue
            type='button'
            onClick={handleContinue}
            disabled={(!selectedCalKey && !newCalName.trim()) || isCreatingCalendar}
          >
            {isCreatingCalendar ? 'Creating…' : 'Continue'}
            <svg width='16' height='16' viewBox='0 0 16 16' fill='none' aria-hidden='true'>
              <path
                d='M3 8 H13 M9 4 L13 8 L9 12'
                stroke='currentColor'
                strokeWidth='1.67'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
            </svg>
          </CalPickerContinue>
        </CalPickerFooter>
      </CalPickerBox>
    </Modal>
  );
};

LoadToCalendarModal.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  accountCode: PropTypes.number,
  // eslint-disable-next-line react/forbid-prop-types
  program: PropTypes.object,
};

LoadToCalendarModal.defaultProps = {
  isOpen: false,
  accountCode: undefined,
  program: null,
};

export default LoadToCalendarModal;
